'use client';
import React, { useState } from 'react';
import { CreditCard, Shield, CheckCircle2, Loader2, Lock, Crown, Zap } from 'lucide-react';
import { toast } from 'sonner';
import Link from 'next/link';
import type { WizardData } from './VisaWizard';

interface Props {
  data: WizardData;
  updateData: (d: Partial<WizardData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const plans = [
  {
    id: 'pay-setup',
    icon: Zap,
    name: 'Visa Setup',
    price: 9.99,
    description: 'Checklist + eligibility guidance + embassy notes',
    features: ['AI document checklist', 'Visa eligibility assessment', 'Embassy notes & processing times', 'PDF checklist download'],
    highlight: false,
  },
  {
    id: 'pay-full',
    icon: Crown,
    name: 'Full Visa Package',
    price: 39.99,
    description: 'Everything — documents, AI writing, validation & PDF bundle',
    features: ['Everything in Visa Setup', 'AI cover letter', 'Travel itinerary', 'Sponsor letter', 'Document validation', 'Complete PDF bundle'],
    highlight: true,
    badge: 'Your current package',
  },
];

export default function Step9Payment({ data, onBack }: Props) {
  const [selectedPlan, setSelectedPlan] = useState('pay-full');
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [cardName, setCardName] = useState('');

  const plan = plans.find((p) => p.id === selectedPlan)!;

  const formatCard = (val: string) => {
    return val.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiry = (val: string) => {
    const clean = val.replace(/\D/g, '').slice(0, 4);
    if (clean.length >= 3) return `${clean.slice(0, 2)} / ${clean.slice(2)}`;
    return clean;
  };

  const handlePay = async () => {
    if (!cardNumber || !expiry || !cvc || !cardName) {
      toast.error('Please fill in all card details');
      return;
    }
    setPaying(true);
    // TODO: Create Stripe PaymentIntent via /api/payments/create-intent and confirm with Stripe.js
    await new Promise((r) => setTimeout(r, 2400));
    setPaying(false);
    setPaid(true);
    toast.success(`Payment of $${plan.price} confirmed — your package is unlocked!`);
  };

  if (paid) {
    return (
      <div className="wizard-card text-center space-y-6 animate-slide-up py-8">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
          <CheckCircle2 size={36} className="text-green-500" />
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-extrabold text-gray-900">Payment confirmed!</h2>
          <p className="text-gray-500 text-sm leading-relaxed max-w-sm mx-auto">
            Your {data.destinationFlag} {data.destinationCountry} visa application package is fully unlocked and ready to download.
          </p>
        </div>
        <div className="bg-green-50 border border-green-100 rounded-xl p-4 text-left space-y-2 max-w-xs mx-auto">
          <p className="text-xs font-bold text-green-800">What&apos;s included</p>
          {plan.features.map((feat) => (
            <div key={`paid-feat-${feat.slice(0, 20)}`} className="flex items-center gap-2">
              <CheckCircle2 size={13} className="text-green-500 shrink-0" />
              <span className="text-xs text-green-700">{feat}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-2 max-w-xs mx-auto">
          <button className="btn-primary w-full justify-center py-3 text-sm">
            <CheckCircle2 size={15} />
            Go to my dashboard
          </button>
          <Link href="/landing-page" className="btn-secondary w-full justify-center py-2.5 text-sm">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-card space-y-6 animate-slide-up">
      {/* Header */}
      <div className="space-y-1">
        <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest">Step 9 of 9</p>
        <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">Complete your payment</h2>
        <p className="text-sm text-gray-500">
          One-time payment — no subscription, no hidden fees.
        </p>
      </div>

      {/* Plan selector */}
      <div className="space-y-2">
        <label className="text-sm font-bold text-gray-700">Select your plan</label>
        <div className="grid sm:grid-cols-2 gap-3">
          {plans.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelectedPlan(p.id)}
              className={`relative flex flex-col gap-3 p-4 rounded-xl border text-left transition-all duration-150 ${
                selectedPlan === p.id
                  ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100' :'border-gray-200 bg-white hover:border-blue-200'
              }`}
            >
              {p.badge && (
                <div className="absolute -top-2.5 left-3">
                  <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">{p.badge}</span>
                </div>
              )}
              <div className="flex items-center gap-2.5 mt-1">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${selectedPlan === p.id ? 'bg-blue-600' : 'bg-gray-100'}`}>
                  <p.icon size={16} className={selectedPlan === p.id ? 'text-white' : 'text-gray-500'} />
                </div>
                <div>
                  <p className={`text-sm font-bold ${selectedPlan === p.id ? 'text-blue-800' : 'text-gray-800'}`}>{p.name}</p>
                  <p className="text-lg font-extrabold text-gray-900 tabular-nums">${p.price}</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 leading-relaxed">{p.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Order summary */}
      <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 space-y-2">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Order summary</p>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">{plan.name}</span>
          <span className="text-sm font-bold text-gray-900 tabular-nums">${plan.price}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">Destination</span>
          <span className="text-xs text-gray-600">{data.destinationFlag} {data.destinationCountry} — {data.visaType}</span>
        </div>
        <div className="border-t border-gray-200 pt-2 flex items-center justify-between">
          <span className="text-sm font-bold text-gray-800">Total</span>
          <span className="text-base font-extrabold text-blue-700 tabular-nums">${plan.price} USD</span>
        </div>
      </div>

      {/* Card form */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <CreditCard size={15} className="text-blue-500" />
          <label className="text-sm font-bold text-gray-700">Card details</label>
          <div className="flex items-center gap-1 ml-auto">
            {['VISA', 'MC', 'AMEX'].map((card) => (
              <span key={`card-brand-${card}`} className="text-xs font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{card}</span>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="cardName" className="text-xs font-semibold text-gray-600">Name on card</label>
          <input
            id="cardName"
            type="text"
            placeholder="Layla Al-Rashidi"
            className="input-field"
            value={cardName}
            onChange={(e) => setCardName(e.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="cardNumber" className="text-xs font-semibold text-gray-600">Card number</label>
          <div className="relative">
            <input
              id="cardNumber"
              type="text"
              placeholder="1234 5678 9012 3456"
              className="input-field font-mono pr-10"
              value={cardNumber}
              onChange={(e) => setCardNumber(formatCard(e.target.value))}
              maxLength={19}
            />
            <Lock size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label htmlFor="expiry" className="text-xs font-semibold text-gray-600">Expiry</label>
            <input
              id="expiry"
              type="text"
              placeholder="MM / YY"
              className="input-field font-mono"
              value={expiry}
              onChange={(e) => setExpiry(formatExpiry(e.target.value))}
              maxLength={7}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="cvc" className="text-xs font-semibold text-gray-600">CVC</label>
            <input
              id="cvc"
              type="text"
              placeholder="123"
              className="input-field font-mono"
              value={cvc}
              onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
              maxLength={4}
            />
          </div>
        </div>
      </div>

      {/* Security note */}
      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-100">
        <Shield size={13} className="text-green-500 shrink-0" />
        <p className="text-xs text-gray-500">
          Payments are processed securely by Stripe. VisaPilot never stores your card details.
        </p>
      </div>

      {/* Pay button */}
      <button
        onClick={handlePay}
        disabled={paying}
        className="btn-primary w-full justify-center py-3.5 text-base animate-pulse-glow"
      >
        {paying ? (
          <Loader2 size={17} className="animate-spin" />
        ) : (
          <>
            <Lock size={15} />
            Pay ${plan.price} — Unlock Full Package
          </>
        )}
      </button>

      <p className="text-xs text-center text-gray-400">
        One-time payment · No subscription · 24-hour refund policy
      </p>
    </div>
  );
}